import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, TextField, Button, Paper, CircularProgress } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuItem from '@mui/material/MenuItem';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import Autocomplete from '@mui/material/Autocomplete';
import api from '../services/api';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FileExplorer from '../pages/FileExplorer';
import { backendUrl } from '../config';

// Cache pour la clé API
let cachedApiKey = null;

// Fonction pour récupérer la clé API
async function getMistralApiKey() {
  // Si la clé est en cache, on la renvoie
  if (cachedApiKey) return cachedApiKey;
  
  try {
    console.log('Récupération de la configuration IA...');
    const response = await fetch(`${backendUrl}/api/config/AI-global`, {
      headers: { 
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Erreur de réponse:', response.status, errorText);
      throw new Error(`Erreur ${response.status} lors de la récupération de la configuration IA`);
    }
    
    const configs = await response.json();
    console.log('Configurations IA reçues:', configs);
    
    // Trouver la configuration Mistral
    const mistralConfig = Array.isArray(configs) 
      ? configs.find(config => config.provider === 'mistral' || config.mistral?.apiKey)
      : null;
    
    if (!mistralConfig) {
      throw new Error('Aucune configuration Mistral trouvée dans la configuration IA');
    }
    
    // Extraire la clé API de la configuration Mistral
    const apiKey = mistralConfig.mistral?.apiKey;
    
    if (!apiKey) {
      throw new Error('Aucune clé API trouvée dans la configuration Mistral');
    }
    
    console.log('Clé API Mistral trouvée');
    
    // Mettre en cache la clé
    cachedApiKey = apiKey;
    return apiKey;
    
  } catch (error) {
    console.error('Erreur getMistralApiKey:', error);
    throw error;
  }
}

// Streaming Ollama response (token by token)
async function proxyIaApiRequest({ target_url, payload, headers = {} }) {
  try {
    // Récupérer la clé API
    const apiKey = await getMistralApiKey();
    
    console.log('Envoi de la requête à:', target_url);
    
    // Préparer les en-têtes avec l'authentification
    const requestHeaders = {
      ...headers,
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    };

    // Faire l'appel à l'API Mistral via le proxy
    const res = await fetch(`${backendUrl}/api/proxy-ia`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ 
        target_url, 
        payload, 
        headers: requestHeaders
      })
    });
    
    if (!res.ok) {
      const errorText = await res.text();
      console.error('Erreur API:', errorText);
      throw new Error(`Erreur API (${res.status}): ${errorText}`);
    }
    
    return res;
  } catch (error) {
    console.error('Erreur dans proxyIaApiRequest:', error);
    throw error;
  }
}

// Version proxy du streaming Ollama
async function streamOllamaResponse({ prompt, model = 'llama3', aiUrl = 'http://localhost:11434/api/chat', onToken, onDone, onError }) {
  try {
    // Vérification stricte avant l'appel proxy
    const payload = {
      model,
      messages: [{ role: 'user', content: prompt }],
      stream: true
    };
    if (!payload.model || !payload.messages || !Array.isArray(payload.messages) || payload.messages.length === 0) {
      if (onError) onError(new Error('Le prompt ou le modèle IA est manquant.'));
      return;
    }
    const res = await proxyIaApiRequest({
      target_url: aiUrl,
      payload,
      headers: { 'Content-Type': 'application/json' }
    });
    if (!res.body) throw new Error('No response body');
    const reader = res.body.getReader();
    let partial = '';
    const decoder = new TextDecoder();
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      for (const line of chunk.split('\n')) {
        if (!line.trim()) continue;
        try {
          const obj = JSON.parse(line);
          const token = obj.message?.content || obj.response || '';
          if (token) {
            partial += token;
            if (onToken) onToken(token, partial);
          }
        } catch (e) {
          // Ignore parse errors (incomplete JSON)
        }
      }
    }
    if (onDone) onDone(partial);
  } catch (err) {
    if (onError) onError(err);
  }
}

// Configuration de base pour Ollama (adapter si besoin)
const OLLAMA_API_URL = process.env.REACT_APP_OLLAMA_API_URL || 'http://localhost:11434/api/generate';
const OLLAMA_MODEL = process.env.REACT_APP_OLLAMA_MODEL || 'llama3';
const API_URL = backendUrl;

const INPUT_OPTIONS = [
  { value: 'custom', label: 'Texte personnalisé' },
  { value: 'prev_output', label: 'Sortie du prompt précédent' },
  { value: 'file', label: 'Fichier' }
];
const OUTPUT_OPTIONS = [
  { value: 'text', label: 'Texte brut' },
  { value: 'json', label: 'JSON (si possible)' }
];

const IA_MODELS = [
  { label: 'Ollama (local)', value: 'ollama', aiUrl: OLLAMA_API_URL, models: ['llama3', 'mistral', 'gemma', 'phi3', 'mixtral', 'codellama'] },
  { label: 'OpenAI GPT-3.5/4', value: 'openai', aiUrl: process.env.REACT_APP_OPENAI_API_URL || '', models: ['gpt-3.5-turbo', 'gpt-4', 'gpt-4o'] },
  { label: 'Mistral (API)', value: 'mistral', aiUrl: process.env.REACT_APP_MISTRAL_API_URL || '', models: ['mistral-tiny', 'mistral-small', 'mistral-medium'] },
  { label: 'Gemini (Google)', value: 'gemini', aiUrl: process.env.REACT_APP_GEMINI_API_URL || '', models: ['gemini-pro'] }
];

function getUserId() {
  const userStr = window.localStorage.getItem('user');
  if (!userStr) return null;
  try {
    const user = JSON.parse(userStr);
    return user.uuid7 || user.uuid || user.id || null;
  } catch {
    return null;
  }
}

async function loadIaConfigsList() {
  const token = window.localStorage.getItem('token');
  try {
    const res = await api.get(`${API_URL}/api/config/AI-global`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    // On suppose que chaque config a un champ 'name' ou 'key' pour l'affichage
    return (res.data || []).map(cfg => ({
      label: cfg.name || cfg.key || 'API IA',
      value: cfg.key || cfg.name,
      ...cfg
    }));
  } catch {
    return [];
  }
}

// Désactivé : la sauvegarde de config IA globale n'est plus autorisée ici
async function saveIaConfigForUser() {
  return;
}

async function loadIaConfigForUser() {
  const uuid7 = getUserId();
  if (!uuid7) return null;
  const token = window.localStorage.getItem('token');
  try {
    const res = await api.get(`${API_URL}/api/config/AI-global`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const configs = res.data
      .filter(cfg => cfg.key === 'ia_config_global' || cfg.key.startsWith('ia_config_'))
      .map(cfg => {
        try {
          return { ...JSON.parse(cfg.value), key: cfg.key, id: cfg.id };
        } catch {
          return { key: cfg.key, id: cfg.id };
        }
      });
    return configs;
  } catch {
    return null;
  }
}

// Fonction pour charger dynamiquement les modèles Ollama
async function fetchOllamaModels(aiUrl = OLLAMA_API_URL) {
  try {
    const url = aiUrl.replace(/\/generate$/, '/tags');
    // N'appelle pas le backend si le provider cible nécessite un payload non vide
    // (ex: 4styl.com/api/chat refuse les payloads vides)
    if (aiUrl.includes('4styl.com')) return [];
    const res = await proxyIaApiRequest({
      target_url: url,
      payload: {},
      headers: { 'Content-Type': 'application/json' }
    });
    if (!res.ok) throw new Error('Erreur chargement modèles Ollama');
    const data = await res.json();
    if (Array.isArray(data.models)) {
      return data.models.map(m => m.name || m.model || m.tag || m);
    }
    if (Array.isArray(data.tags)) {
      return data.tags.map(t => t.name || t.tag || t);
    }
    return [];
  } catch (e) {
    return [];
  }
}

export default function AgentApp() {
  // ... états et hooks existants ...

  // Chargement automatique de la config IA par défaut utilisateur
  useEffect(() => {
    const userStr = window.localStorage.getItem('user');
    let uuid7 = null;
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        uuid7 = user.uuid7 || user.uuid || user.id || null;
      } catch {}
    }
    if (!uuid7) return;
    const token = window.localStorage.getItem('token');
    // Récupérer la config par défaut
    fetch(`${backendUrl}/api/config/AI-default/${uuid7}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(cfg => {
        if (cfg.provider) {
          setIaProvider(cfg.provider);
          localStorage.setItem('ia_provider', cfg.provider);
        }
        if (cfg.model) {
          setIaModel(cfg.model);
          localStorage.setItem('ia_model', cfg.model);
        }
        if (cfg.endpoint !== undefined) {
          setCustomApiUrl(cfg.endpoint);
          localStorage.setItem('ia_api_url', cfg.endpoint || '');
        }
        // Synchroniser la sélection dans la liste déroulante si présente
        if (Array.isArray(iaConfigs) && iaConfigs.length > 0) {
          const idx = iaConfigs.findIndex(cfgItem =>
            (cfgItem.iaModel === cfg.model || cfgItem.model === cfg.model) &&
            (cfgItem.iaProvider === cfg.provider || cfgItem.provider === cfg.provider)
          );
          if (idx >= 0) {
            setSelectedConfigIdx(idx);
            setSelectedIa(iaConfigs[idx].value);
          }
        }
      })
      .catch(() => {});
  }, []);

  const [tab, setTab] = useState(0); // 0: Agent, 1: Config
  // Cascade prompts state
  const [prompts, setPrompts] = useState([
    { input: '', output: '', loading: false, error: '', inputType: 'custom', outputType: 'text', prevPromptIdx: null }
  ]);
  const [iaProvider, setIaProvider] = useState(() => localStorage.getItem('ia_provider') || 'ollama');
  const [iaModel, setIaModel] = useState(() => localStorage.getItem('ia_model') || 'llama3');
  const [customApiUrl, setCustomApiUrl] = useState(() => localStorage.getItem('ia_api_url') || OLLAMA_API_URL);
  const [selectedConfigIdx, setSelectedConfigIdx] = useState(() => {
  const defaultModel = localStorage.getItem('ia_model');
  const defaultProvider = localStorage.getItem('ia_provider');
  if (Array.isArray(window.__IA_CONFIGS__) && window.__IA_CONFIGS__.length > 0) {
    const idx = window.__IA_CONFIGS__.findIndex(cfg =>
      (cfg.iaModel === defaultModel || cfg.model === defaultModel) &&
      (cfg.iaProvider === defaultProvider || cfg.provider === defaultProvider)
    );
    return idx >= 0 ? idx : 0;
  }
  return 0;
});
  // Nouveaux états pour la validation de la config IA sélectionnée
  const [configValid, setConfigValid] = useState(true);
  const [configWarning, setConfigWarning] = useState('');
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);

  const [iaConfigs, setIaConfigs] = useState([]); // Liste des configs IA disponibles
  const [selectedIa, setSelectedIa] = useState('');

  const [configName, setConfigName] = useState('');

  const [editingKey, setEditingKey] = useState(null);

  const fileInputRefs = useRef([]);

  const [ollamaModels, setOllamaModels] = useState([]);
  const [ollamaModelLoading, setOllamaModelLoading] = useState(false);

  // Explorer-like dialog to select a prompt session file from backend
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [explorerPath, setExplorerPath] = useState('/');
  const [explorerFiles, setExplorerFiles] = useState([]);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [explorerError, setExplorerError] = useState('');

  // Ajoute l'explorateur pour sauvegarder la session de prompts
  const [saveExplorerOpen, setSaveExplorerOpen] = useState(false);
  const [saveExplorerPath, setSaveExplorerPath] = useState('/');
  const [saveExplorerFiles, setSaveExplorerFiles] = useState([]);
  const [saveExplorerLoading, setSaveExplorerLoading] = useState(false);
  const [saveExplorerError, setSaveExplorerError] = useState('');
  const [saveName, setSaveName] = useState(() => `prompts-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.json`);

  async function fetchSaveExplorerFiles(path) {
    setSaveExplorerLoading(true);
    setSaveExplorerError('');
    try {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = path === '/' ? '' : path.replace(/^\//, '');
      const res = await api.get(`${backendUrl}/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveExplorerFiles(res.data.filter(f => f.type === 'folder' || f.type === 'file'));
    } catch {
      setSaveExplorerFiles([]);
    } finally {
      setSaveExplorerLoading(false);
    }
  }

  function handleOpenSaveExplorer() {
    setSaveExplorerOpen(true);
    setSaveExplorerPath('/');
    setSaveName(`prompts-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.json`);
    fetchSaveExplorerFiles('/');
  }

  function handleSaveExplorerNavigate(folder) {
    const newPath = saveExplorerPath === '/' ? `/${folder}` : `${saveExplorerPath}/${folder}`;
    setSaveExplorerPath(newPath);
    fetchSaveExplorerFiles(newPath);
  }

  function handleSaveExplorerGoUp() {
    if (saveExplorerPath === '/') return;
    const parts = saveExplorerPath.split('/').filter(Boolean);
    parts.pop();
    const newPath = parts.length === 0 ? '/' : '/' + parts.join('/');
    setSaveExplorerPath(newPath);
    fetchSaveExplorerFiles(newPath);
  }

  async function handleSavePromptsToBackend() {
    const userStr = window.localStorage.getItem('user');
    let uuid7 = null;
    if (userStr) {
      try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
    }
    if (!uuid7) { alert('Utilisateur non trouvé'); return; }
    const token = window.localStorage.getItem('token');
    let relPath = saveExplorerPath === '/' ? saveName : saveExplorerPath.replace(/^\//, '') + '/' + saveName;
    try {
      await api.post(`${backendUrl}/save-file`, {
        uuid7,
        path: relPath,
        content: JSON.stringify(prompts, null, 2),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveExplorerOpen(false);
      alert('Session de prompts sauvegardée !');
    } catch (e) {
      alert("Erreur lors de la sauvegarde des prompts : " + (e?.message || ''));
    }
  }

  async function fetchExplorerFiles(path) {
    setExplorerLoading(true);
    setExplorerError('');
    try {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = path === '/' ? '' : path.replace(/^\//, '');
      const res = await api.get(`${backendUrl}/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExplorerFiles(res.data);
    } catch (e) {
      setExplorerError("Erreur lors du chargement des fichiers.");
      setExplorerFiles([]);
    } finally {
      setExplorerLoading(false);
    }
  }

  function handleOpenFromExplorer() {
    setExplorerOpen(true);
    setExplorerPath('/');
    fetchExplorerFiles('/');
  }

  async function handleExplorerSelectFile(file) {
    // Lecture backend
    try {
      setExplorerLoading(true);
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = explorerPath === '/' ? file.name : explorerPath.replace(/^\//, '') + '/' + file.name;
      const res = await api.get(`${API_URL}/file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const loadedPrompts = JSON.parse(res.data.content);
      if (!Array.isArray(loadedPrompts)) throw new Error('Format de fichier invalide');
      setPrompts(loadedPrompts);
      setExplorerOpen(false);
      alert('Session de prompts chargée !');
    } catch (e) {
      setExplorerError("Erreur lors de la lecture du fichier.");
    } finally {
      setExplorerLoading(false);
    }
  }

  function handleExplorerNavigate(folder) {
    const newPath = explorerPath === '/' ? `/${folder}` : `${explorerPath}/${folder}`;
    setExplorerPath(newPath);
    fetchExplorerFiles(newPath);
  }

  function handleExplorerGoUp() {
    if (explorerPath === '/') return;
    const parts = explorerPath.split('/').filter(Boolean);
    parts.pop();
    const newPath = parts.length === 0 ? '/' : '/' + parts.join('/');
    setExplorerPath(newPath);
    fetchExplorerFiles(newPath);
  }

  // Ajoute l'explorateur pour sélectionner un fichier
  const [selectedFilePath, setSelectedFilePath] = useState('');
  const [activePromptIdx, setActivePromptIdx] = useState(null);

  function handleOpenExplorer() {
    setExplorerOpen(true);
  }

  function handleExplorerSelectFile(file) {
    // On suppose que file possède .name et .type
    let relPath = explorerPath === '/' ? file.name : explorerPath.replace(/^\//, '') + '/' + file.name;
    setSelectedFilePath(relPath);
    setExplorerOpen(false);
  }

  // Validation de la sélection IA à chaque changement
  useEffect(() => {
    if (!iaProvider || !iaModel) {
      setConfigValid(false);
      setConfigWarning("Sélectionnez un provider et un modèle IA.");
    } else {
      setConfigValid(true);
      setConfigWarning("");
    }
  }, [iaProvider, iaModel]);

  // Ajout d'un bouton pour insérer le chemin sélectionné dans le prompt courant
  const fileInputRef = useRef();

  function handleLocalFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFilePath(file.name); // Affiche juste le nom, ou utilisez file.path si disponible (web: non)
    // Pour lire le contenu du fichier local et l'insérer dans le prompt courant :
    const reader = new FileReader();
    reader.onload = function(evt) {
      const content = evt.target.result;
      setPrompts(prompts => {
        const idx = activePromptIdx ?? 0;
        const newPrompts = [...prompts];
        newPrompts[idx] = {
          ...newPrompts[idx],
          input: newPrompts[idx].input ? newPrompts[idx].input + '\n' + content : content
        };
        return newPrompts;
      });
    };
    reader.readAsText(file);
  }

  async function handleSendPrompt(idx) {
    // Always use the currently selected config for sending
    let provider = iaProvider;
    let model = iaModel;
    let aiUrl = customApiUrl;
    if (iaConfigs && iaConfigs.length > 0 && typeof selectedConfigIdx === 'number' && selectedConfigIdx >= 0 && iaConfigs[selectedConfigIdx]) {
      const cfg = iaConfigs[selectedConfigIdx];
      provider = cfg.iaProvider || cfg.provider || provider;
      model = cfg.iaModel || cfg.model || model;
      aiUrl = cfg.customApiUrl || cfg.aiUrl || aiUrl;
    }

    // Vérification stricte avant envoi
    if (!model || typeof model !== 'string' || !model.trim()) {
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[idx].error = 'Aucun modèle IA sélectionné.';
        newPrompts[idx].loading = false;
        return newPrompts;
      });
      return;
    }
    if (!aiUrl || typeof aiUrl !== 'string' || !/^https?:\/\//.test(aiUrl)) {
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[idx].error = "L'URL de l'API IA est invalide ou manquante.";
        newPrompts[idx].loading = false;
        return newPrompts;
      });
      return;
    }

    setPrompts(prompts => {
      const newPrompts = [...prompts];
      newPrompts[idx].output = '';
      newPrompts[idx].loading = true;
      newPrompts[idx].error = '';
      return newPrompts;
    });
    let prompt = prompts[idx].input;
    const prevIdx = prompts[idx].prevPromptIdx;
    if (typeof prevIdx === 'number' && prevIdx >= 0 && prevIdx < idx) {
      const prevPrompt = prompts[prevIdx].input;
      prompt = prevPrompt + '\n' + prompt;
    }
    try {
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[idx].loading = true;
        newPrompts[idx].error = '';
        return newPrompts;
      });
      const payload = {
        model: iaModel,
        messages: [{ role: 'user', content: prompt }],
        stream: false
      };
      // Chercher une config utilisateur avec endpoint pour ce modèle (match sur model, iaModel ou name ET provider ou iaProvider)
      let modelConfig = iaConfigs && iaConfigs.find(cfg => {
        const providerMatch = (cfg.iaProvider || cfg.provider) === provider;
        const modelMatch = [cfg.iaModel, cfg.model, cfg.name].includes(model);
        return providerMatch && modelMatch;
      });
      let targetUrl = modelConfig && modelConfig.endpoint ? modelConfig.endpoint : (provider === 'ollama' ? aiUrl.replace(/\/generate$/, '/chat') : aiUrl);
      //console.log('[IAAPP] handleSendPrompt', { targetUrl, model, provider, modelConfig, payload });
      const res = await proxyIaApiRequest({
        target_url: targetUrl,
        payload,
        headers: { 'Content-Type': 'application/json' }
      });
      let output = '';
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await res.json();
        output = data.choices?.[0]?.message?.content || data.candidates?.[0]?.content || data.generated_text || data.message?.content || data.response || data.message || JSON.stringify(data);
      } else {
        const text = await res.text();
        try {
          const data = JSON.parse(text);
          output = data.choices?.[0]?.message?.content || data.candidates?.[0]?.content || data.generated_text || data.message?.content || data.response || data.message || text;
        } catch {
          output = text;
        }
      }
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[idx].output = output;
        newPrompts[idx].loading = false;
        return newPrompts;
      });
    } catch (err) {
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[idx].error = err.message || 'Erreur API';
        newPrompts[idx].loading = false;
        return newPrompts;
      });
    }
  }

  async function handleSendPromptsCascade(idx) {
    for (let i = 0; i <= idx; i++) {
      // Si déjà une réponse, on saute sauf si loading ou input changé
      if (prompts[i].loading) return;
      setPrompts(prompts => {
        const newPrompts = [...prompts];
        newPrompts[i].output = '';
        newPrompts[i].loading = true;
        newPrompts[i].error = '';
        return newPrompts;
      });
      let prompt = prompts[i].input;
      const prevIdx = prompts[i].prevPromptIdx;
      if (typeof prevIdx === 'number' && prevIdx >= 0 && prevIdx < i) {
        const prevPrompt = prompts[prevIdx].input;
        prompt = prevPrompt + '\n' + prompt;
      }
      try {
        setPrompts(prompts => {
          const newPrompts = [...prompts];
          newPrompts[i].loading = true;
          newPrompts[i].error = '';
          return newPrompts;
        });
        const payload = {
          model: iaModel,
          messages: [{ role: 'user', content: prompt }],
          stream: false
        };
        // Chercher une config utilisateur avec endpoint pour ce modèle (match sur model, iaModel ou name ET provider ou iaProvider)
        let modelConfig = iaConfigs && iaConfigs.find(cfg => {
          const providerMatch = (cfg.iaProvider || cfg.provider) === iaProvider;
          const modelMatch = [cfg.iaModel, cfg.model, cfg.name].includes(iaModel);
          return providerMatch && modelMatch;
        });
        let targetUrl = modelConfig && modelConfig.endpoint ? modelConfig.endpoint : (iaProvider === 'ollama' ? customApiUrl.replace(/\/generate$/, '/chat') : customApiUrl);
        //console.error('[IAAPP] handleSendPromptsCascade', { targetUrl, iaModel, iaProvider, modelConfig, payload });
        const res = await proxyIaApiRequest({
          target_url: targetUrl,
          payload,
          headers: { 'Content-Type': 'application/json' }
        });
        let output = '';
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await res.json();
          output = data.choices?.[0]?.message?.content || data.candidates?.[0]?.content || data.generated_text || data.message?.content || data.response || data.message || JSON.stringify(data);
        } else {
          const text = await res.text();
          try {
            const data = JSON.parse(text);
            output = data.choices?.[0]?.message?.content || data.candidates?.[0]?.content || data.generated_text || data.message?.content || data.response || data.message || text;
          } catch { output = text; }
        }
        setPrompts(prompts => {
          const newPrompts = [...prompts];
          newPrompts[i].output = output;
          newPrompts[i].loading = false;
          return newPrompts;
        });
      } catch (err) {
        setPrompts(prompts => {
          const newPrompts = [...prompts];
          newPrompts[i].error = err.message || 'Erreur API';
          newPrompts[i].loading = false;
          return newPrompts;
        });
        break;
      }
    }
  }

  function handleAddPrompt() {
    setPrompts([...prompts, { input: '', output: '', loading: false, error: '', inputType: 'custom', outputType: 'text', prevPromptIdx: null }]);
  }
  function handleRemovePrompt(idx) {
    if (prompts.length === 1) return;
    setPrompts(prompts.filter((_, i) => i !== idx));
  }
  function handleInputChange(idx, value) {
    setPrompts(prompts => prompts.map((p, i) => i === idx ? { ...p, input: value } : p));
  }
  function handleInputTypeChange(idx, value) {
    setPrompts(prompts => prompts.map((p, i) => i === idx ? { ...p, inputType: value } : p));
  }
  function handleOutputTypeChange(idx, value) {
    setPrompts(prompts => prompts.map((p, i) => i === idx ? { ...p, outputType: value } : p));
  }
  function handleOutputToInput(idx) {
    if (prompts[idx].output) {
      handleInputChange(idx + 1, prompts[idx].output);
    }
  }

  function handleIaProviderChange(event, value) {
    if (!value) return;
    setIaProvider(value.value);
    setIaModel(value.models[0]);
    setCustomApiUrl(value.aiUrl || '');
    localStorage.setItem('ia_provider', value.value);
    localStorage.setItem('ia_model', value.models[0]);
    localStorage.setItem('ia_api_url', value.aiUrl || '');
    setConfigSaved(false);
  }
  function handleIaModelChange(event, value) {
    if (!value) return;
    setIaModel(value);
    localStorage.setItem('ia_model', value);
    // Find the selected provider config or IA_MODELS entry
    let aiUrl = '';
    // Try to find in iaConfigs (user configs)
    let config = iaConfigs.find(cfg => cfg.iaModel === value && cfg.iaProvider === iaProvider);
    if (config && config.customApiUrl) {
      aiUrl = config.customApiUrl;
    } else {
      // Fallback: try to find in IA_MODELS (static)
      const staticProvider = IA_MODELS.find(m => m.value === iaProvider);
      if (staticProvider && staticProvider.aiUrl) {
        aiUrl = staticProvider.aiUrl;
      }
    }
    setCustomApiUrl(aiUrl);
    localStorage.setItem('ia_api_url', aiUrl);
    setConfigSaved(false);
  }
  function handleCustomApiUrlChange(e) {
    setCustomApiUrl(e.target.value);
    localStorage.setItem('ia_api_url', e.target.value);
    setConfigSaved(false);
  }

  async function handleSaveConfig(e) {
    e.preventDefault();
    const key = editingKey || (configName ? `ia_config_${configName.replace(/\s+/g, '_').toLowerCase()}` : 'ia_config_global');
    await saveIaConfigForUser({ iaProvider, iaModel, customApiUrl, key });
    setConfigSaved(true);
    setTimeout(() => setConfigSaved(false), 2000);
    setEditingKey(null);
    loadIaConfigsList();
  }

  function handleFileInputChange(idx, e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      handleInputChange(idx, event.target.result);
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  function handleOpenFileDialog(idx) {
    if (fileInputRefs.current[idx]) fileInputRefs.current[idx].click();
  }

  // Ajoute une fonction pour charger une session de prompts depuis un fichier backend
  async function handleLoadPromptsFromFile() {
    const userStr = window.localStorage.getItem('user');
    let uuid7 = null;
    if (userStr) {
      try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
    }
    if (!uuid7) { alert('Utilisateur non trouvé'); return; }
    const token = window.localStorage.getItem('token');
    const path = window.prompt('Nom du fichier à charger ?', 'prompts-YYYY-MM-DD-HH-mm-ss.json');
    if (!path) return;
    try {
      const res = await api.get(`/file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(path)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const loadedPrompts = JSON.parse(res.data.content);
      if (!Array.isArray(loadedPrompts)) throw new Error('Format de fichier invalide');
      setPrompts(loadedPrompts);
      alert('Session de prompts chargée !');
    } catch (e) {
      alert("Erreur lors du chargement des prompts : " + (e?.message || ''));
    }
  }

  useEffect(() => {
    setLoadingConfig(true);
    loadIaConfigsList().then(cfg => {
      setIaConfigs(cfg);
      setLoadingConfig(false);
      // Forcer une sélection valide dès l'ouverture si possible
      if (cfg && cfg.length > 0) {
        const firstCfg = cfg[0];
        if (firstCfg.iaProvider || firstCfg.provider) setIaProvider(firstCfg.iaProvider || firstCfg.provider);
        if (firstCfg.iaModel || firstCfg.model || firstCfg.name) setIaModel(firstCfg.iaModel || firstCfg.model || firstCfg.name);
        if (firstCfg.customApiUrl || firstCfg.endpoint) setCustomApiUrl(firstCfg.customApiUrl || firstCfg.endpoint);
      }
    });
  }, []); // eslint-disable-next-line

  useEffect(() => {
    if (iaProvider === 'ollama') {
      setOllamaModelLoading(true);
      fetchOllamaModels(customApiUrl).then(models => {
        setOllamaModels(models);
        setOllamaModelLoading(false);
      });
    }
  }, [iaProvider, customApiUrl]);

  return (
    <Box sx={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      <Paper elevation={3} sx={{
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100vw',
        height: '100vh',
        borderRadius: 0
      }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab icon={<SmartToyIcon />} label="Agent IA" />
          <Tab icon={<SettingsIcon />} label="Configuration" />
        </Tabs>
        {tab === 0 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Configuration utilisée :</Typography>
              <Typography variant="body1" color="primary" sx={{ fontWeight: 600 }}>
  {iaProvider && iaModel
    ? `${iaProvider} (${iaModel})`
    : 'Aucune configuration'}
</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <SmartToyIcon color="primary" fontSize="large" />
              <Typography variant="h5">Agent IA en cascade</Typography>
            </Box>
            <Box sx={{ overflowY: 'auto', maxHeight: 480, pr: 1 }}>
              {prompts.map((prompt, idx) => (
                <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="subtitle2" color="primary">Prompt {idx + 1}</Typography>
                    {prompts.length > 1 && (
                      <Button size="small" color="error" onClick={() => handleRemovePrompt(idx)} startIcon={<DeleteIcon />} sx={{ ml: 1 }}>Supprimer</Button>
                    )}
                  </Box>
                  <Box display="flex" gap={2} mb={1}>
                    <TextField
                      select
                      label="Source de l'entrée"
                      value={prompt.inputType}
                      onChange={e => handleInputTypeChange(idx, e.target.value)}
                      sx={{ minWidth: 180 }}
                    >
                      {INPUT_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Format de sortie"
                      value={prompt.outputType}
                      onChange={e => handleOutputTypeChange(idx, e.target.value)}
                      sx={{ minWidth: 150 }}
                    >
                      {OUTPUT_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  {/* Sélection d'un prompt précédent */}
                  {idx > 0 && (
                    <TextField
                      select
                      label="Prompt précédent à combiner"
                      value={prompt.prevPromptIdx ?? ''}
                      onChange={e => {
                        setPrompts(prompts => prompts.map((pp, i) => i === idx ? { ...pp, prevPromptIdx: Number(e.target.value) } : pp));
                      }}
                      sx={{ minWidth: 220, mb: 2 }}
                    >
                      <MenuItem value="">Aucun</MenuItem>
                      {prompts.slice(0, idx).map((pp, j) => (
                        <MenuItem key={j} value={j}>{`Prompt ${j + 1}`}</MenuItem>
                      ))}
                    </TextField>
                  )}
                  {/* Zone de texte "Entrée" + Import fichier texte */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <TextField
                      label="Entrée"
                      value={prompt.input}
                      onChange={e => handleInputChange(idx, e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      sx={{ mb: 0 }}
                      disabled={prompt.inputType === 'file'}
                    />
                    {/* Importer un fichier texte */}
                    <input
                      type="file"
                      accept=".txt,text/plain"
                      style={{ display: 'none' }}
                      id={`import-txt-${idx}`}
                      onChange={e => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const reader = new window.FileReader();
                        reader.onload = evt => {
                          handleInputChange(idx, evt.target.result);
                        };
                        reader.readAsText(file);
                        e.target.value = '';
                      }}
                    />
                    <label htmlFor={`import-txt-${idx}`}>
                      <Button variant="outlined" component="span" startIcon={<FolderOpenIcon />}>
                        Importer un fichier texte
                      </Button>
                    </label>
                    {prompt.inputType === 'file' && (
                      <>
                        <input
                          type="file"
                          accept=".txt,.md,.json,.js,.py,.csv,.log,.xml,.html,.css,.yaml,.yml,.ini,.sh,.bat,.c,.cpp,.java,.ts,.tsx,.jsx,.pdf"
                          style={{ display: 'none' }}
                          ref={el => fileInputRefs.current[idx] = el}
                          onChange={e => handleFileInputChange(idx, e)}
                        />
                        <Button onClick={() => handleOpenFileDialog(idx)} startIcon={<FolderOpenIcon />} variant="outlined">
                          Choisir un fichier
                        </Button>
                      </>
                    )}
                  </Box>
                  {/* Bouton envoyer */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => idx === prompts.length - 1 ? handleSendPromptsCascade(idx) : handleSendPrompt(idx)}
                    disabled={prompts[idx].loading || !configValid}
                    sx={{ mt: 1, mb: 1 }}
                  >
                    {prompts[idx].loading ? <CircularProgress size={20} /> : 'Envoyer'}
                  </Button>
                  {!configValid && (
                    <Typography color="error" fontSize={14} sx={{ mt: 1 }}>{configWarning}</Typography>
                  )}
                  {/* Affichage de la sortie */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="secondary">Réponse IA :</Typography>
                    <Paper variant="outlined" sx={{ p: 2, minHeight: 80, bgcolor: '#fff', color: '#222', fontFamily: 'monospace', fontSize: 16 }}>
                      {prompt.loading ? (
                        <span style={{ color: '#888' }}>Réponse en cours...</span>
                      ) : prompt.error ? (
                        <span style={{ color: 'red' }}>{prompt.error}</span>
                      ) : prompt.output ? (
                        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{prompt.output}</span>
                      ) : (
                        <span style={{ color: '#aaa' }}>Aucune réponse à afficher.</span>
                      )}
                    </Paper>
                    <Button size="small" sx={{ mt: 1 }} onClick={() => handleOutputToInput(idx)}>
                      Utiliser cette sortie comme entrée du prompt suivant
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Button variant="outlined" onClick={() => { setExplorerOpen(true); setActivePromptIdx(idx); }}>
                      Parcourir les fichiers
                    </Button>
                    <Button variant="outlined" onClick={() => { fileInputRef.current && fileInputRef.current.click(); setActivePromptIdx(idx); }}>
                      Choisir un fichier local
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      style={{ display: 'none' }}
                      onChange={handleLocalFileChange}
                    />
                    {selectedFilePath && activePromptIdx === idx && (
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                          setPrompts(prompts => {
                            const newPrompts = [...prompts];
                            newPrompts[idx] = {
                              ...newPrompts[idx],
                              input: newPrompts[idx].input ? newPrompts[idx].input + ' ' + selectedFilePath : selectedFilePath
                            };
                            return newPrompts;
                          });
                        }}
                      >
                        Insérer le chemin
                      </Button>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 2 }}>
              <Button variant="outlined" color="primary" onClick={handleOpenSaveExplorer}>
                Sauvegarder la session de prompts
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleOpenFromExplorer}>
                Ouvrir une session de prompts
              </Button>
            </Box>
            <Button onClick={handleAddPrompt} startIcon={<AddIcon />} variant="outlined" color="secondary">Ajouter un prompt</Button>
          </>
        )}
        {tab === 1 && (
          <form onSubmit={handleSaveConfig} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <SettingsIcon color="primary" fontSize="large" />
              <Typography variant="h6">Configuration IA</Typography>
            </Box>
            {loadingConfig ? (
              <Typography color="text.secondary" sx={{ mb: 2 }}>Chargement de la configuration…</Typography>
            ) : null}
            <Autocomplete
              options={iaConfigs}
              value={iaConfigs[selectedConfigIdx] || null}
              onChange={(_, v) => {
                if (!v) return;
                const idx = iaConfigs.findIndex(cfg => cfg.value === v.value);
                setSelectedConfigIdx(idx);
                if (iaConfigs[idx]) {
                  setIaProvider(iaConfigs[idx].iaProvider || iaConfigs[idx].provider || 'ollama');
                  setIaModel(iaConfigs[idx].iaModel || iaConfigs[idx].model || 'llama3');
                  setCustomApiUrl(iaConfigs[idx].customApiUrl || iaConfigs[idx].aiUrl || '');
                }
              }}
              getOptionLabel={opt => opt.label}
              renderInput={params => <TextField {...params} label="Choisir une IA" />}
              sx={{ mb: 2, width: 320 }}
            />
            
<Button variant="outlined" color="secondary" sx={{ mt: 1 }} onClick={async () => {
  // Récupérer l'UUID7 de l'utilisateur
  const userStr = window.localStorage.getItem('user');
  let uuid7 = null;
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      uuid7 = user.uuid7 || user.uuid || user.id || null;
    } catch {}
  }
  if (!uuid7) {
    alert("Impossible de trouver l'identifiant utilisateur (uuid7)");
    return;
  }
  // Construire la config à sauvegarder
  const config = {
    provider: iaProvider,
    model: iaModel,
    endpoint: customApiUrl
  };
  const token = window.localStorage.getItem('token');
  try {
    await api.put(`${API_URL}/api/config/AI-default/${uuid7}`, config, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setConfigSaved(true);
    setTimeout(() => setConfigSaved(false), 2000);
  } catch (e) {
    alert("Erreur lors de la sauvegarde du modèle par défaut");
  }
}}>
  Utiliser comme modèle par défaut
</Button>
            {configSaved ? <Typography color="success.main" sx={{ mt: 1 }}>Configuration sauvegardée !</Typography> : null}
          </form>
        )}
      </Paper>
      <Dialog open={explorerOpen} onClose={() => setExplorerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Ouvrir une session de prompts</DialogTitle>
        <DialogContent>
          {explorerLoading && <CircularProgress size={24} />}
          {explorerError && <Typography color="error">{explorerError}</Typography>}
          <Breadcrumbs sx={{ mb: 1 }}>
            <Button size="small" onClick={handleExplorerGoUp} disabled={explorerPath === '/'}>↑</Button>
            <Typography variant="caption">{explorerPath}</Typography>
          </Breadcrumbs>
          <List>
            {explorerFiles.map(file => (
              <ListItem button key={file.name} onClick={async () => file.type === 'folder' ? handleExplorerNavigate(file.name) : await handleExplorerSelectFile(file)}>
                <ListItemIcon>{file.type === 'folder' ? <FolderIcon /> : <InsertDriveFileIcon />}</ListItemIcon>
                <ListItemText primary={file.name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExplorerOpen(false)}>Annuler</Button>
        </DialogActions>
      </Dialog>
      {/* Dialog d'exploration pour sauvegarder la session de prompts */}
      <Dialog open={saveExplorerOpen} onClose={() => setSaveExplorerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Sauvegarder la session de prompts</DialogTitle>
        <DialogContent>
          {saveExplorerLoading && <CircularProgress size={24} />}
          {saveExplorerError && <Typography color="error">{saveExplorerError}</Typography>}
          <Breadcrumbs sx={{ mb: 1 }}>
            <Button size="small" onClick={handleSaveExplorerGoUp} disabled={saveExplorerPath === '/'}>↑</Button>
            <Typography variant="caption">{saveExplorerPath}</Typography>
          </Breadcrumbs>
          <List>
            {saveExplorerFiles.filter(f => f.type === 'folder').map(folder => (
              <ListItem button key={folder.name} onClick={() => handleSaveExplorerNavigate(folder.name)}>
                <ListItemIcon><FolderIcon /></ListItemIcon>
                <ListItemText primary={folder.name} />
              </ListItem>
            ))}
          </List>
          <TextField
            label="Nom du fichier"
            value={saveName}
            onChange={e => setSaveName(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveExplorerOpen(false)}>Annuler</Button>
          <Button onClick={handleSavePromptsToBackend} variant="contained">Sauvegarder</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={explorerOpen} onClose={() => setExplorerOpen(false)} maxWidth="md" fullWidth>
        <FileExplorer
          currentUser={null} // ou passez l'utilisateur courant si besoin
          onOpenApp={(_, file) => handleExplorerSelectFile(file)}
        />
      </Dialog>
    </Box>
  );
}
